// src/modulos/Cadastros/ClientsPage.js

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import usePagePermissions from '../../hooks/usePagePermissions';
import styles from './Registers.module.css';
import { countryOptions } from '../../data/countries';
import { db } from '../../firebaseConfig';
import { collection, query, where, getDocs, runTransaction } from 'firebase/firestore';
import { FiEdit, FiTrash2, FiPlus, FiMapPin } from 'react-icons/fi';
import { FaBuilding, FaUserTie, FaClipboard, FaMoneyCheck, FaCreditCard, FaBalanceScale } from "react-icons/fa";
import { HiOutlineIdentification } from "react-icons/hi2";
import { FaPeopleGroup } from "react-icons/fa6";
import { MdAccountBalance } from "react-icons/md";

// Common Components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
    Toast,
} from '../../components/common';

// hooks
import useToast from '../../hooks/useToast';
import usePagination from '../../hooks/usePagination';

// --- 1. Colecoes do Banco de Dados ---
const CLIENTS_COLLECTION = 'clients';
const BILLING_CONDITIONS_COLLECTION = 'parameters/data/billing-conditions';
const REGIONS_COLLECTION = 'parameters/data/regioes';
const ROLES_COLLECTION = 'parameters/data/cargos';
const ACCOUNT_EXECUTIVES_COLLECTION = 'account-executives';

const PAYMENT_DOC_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'documento-pagamento',
};

const ADDRESS_TYPE_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-endereco'
};

const ITEMS_PER_PAGE = 10;

// --- 2. Opcoes Estaticas (Dropdowns) ---
const DIAS_SEMANA_OPTIONS = [
    { value: '', label: '- Selecione -' },
    { value: 'Segunda', label: 'Segunda-feira' },
    { value: 'Terca', label: 'Terca-feira' },
    { value: 'Quarta', label: 'Quarta-feira' },
    { value: 'Quinta', label: 'Quinta-feira' },
    { value: 'Sexta', label: 'Sexta-feira' },
];

const TIPO_DOCUMENTO_OPTIONS = [
    { value: 'CNPJ', label: 'CNPJ' },
    { value: 'CPF', label: 'CPF' },
    { value: 'Tax ID', label: 'Tax ID (Internacional)' },
    { value: 'Passaporte', label: 'Passaporte' },
];

const TIPO_CLIENTE_OPTIONS = [
    { value: 'PADRAO', label: 'PADRAO' },
    { value: 'ESPECIAL', label: 'ESPECIAL' },
    { value: 'VIP', label: 'VIP' },
];

const REGIME_APURACAO_OPTIONS = [
    'Lucro Presumido', 'Lucro Real', 'Simples',
    'Super Simples', 'Pessoa Fisica', 'Micro Empreendedor Individual',
];

const FISCAL_OPTIONS = [
    { value: 'isentoIPI', label: 'Isento IPI' },
    { value: 'isentoICMS', label: 'Isento ICMS' },
    { value: 'isentoPISCOFINS', label: 'Isento PIS/COFINS' },
    { value: 'geraPreFaturamento', label: 'Gera Pre-faturamento' },
    { value: 'contratoFornecimento', label: 'Contrato de Fornecimento' },
    { value: 'vendaConsumo', label: 'Venda Consumo' },
    { value: 'naoContribuinteICMS', label: 'Nao contribuinte ICMS' },
    { value: 'descontoSTIPI', label: 'Desconto ST / IPI' },
    { value: 'isentoICMSST', label: 'Isento ICMS ST' },
    { value: 'expurgaICMSBase', label: 'Expurga ICMS base PIS/COFINS' },
    { value: 'utilizarB2B', label: 'Utilizar B2B' },
    { value: 'qualidadeAssegurada', label: 'Qualidade Assegurada' },
    { value: 'regimeEspecialIsencaoST', label: 'Regime especial c/ isencao de ST' },
];

const BANK_OPTIONS = [
    { value: 'bancoEnviaBoleto', label: 'Banco envia Boleto' },
    { value: 'enviaParaProtesto', label: 'Envia para Protesto' }
];

const LOGISTICS_OPTIONS = [
    { value: 'restricaoEntregaCaminhao', label: 'Restricao de entrega com caminhao' },
    { value: 'clienteEstoqueTerceirista', label: 'Utiliza estoque do Terceirista' }
];

const TABS = {
    GERAL: 'Dados Gerais',
    FISCAL: 'Fiscal / Credito',
    ENDERECOS: 'Enderecos / Pessoas',
};

// --- 3. Modelos Padrao ---
const DEFAULT_CLIENT_MODEL = {
    codigo: '', situacao: 'Ativo', tipoPessoa: 'CNPJ',
    cnpjCpf: '', inscricaoEstadual: '', razaoSocial: '', nomeFantasia: '', cnae: '',
    regiaoId: '', tipo: 'PADRAO', grupoClientes: '',
    formaPagamentoId: '', condicaoFaturamentoId: '', diaSemanaPagamento: '', pais: '',
    tipoDolar: 'medio', valorDolar: '', ultimoDolarFaturado: '',
    website: '', tempoEmpresa: '', encargos: '', retencaoIR: '',
    isentoIPI: false, isentoICMS: false, isentoPISCOFINS: false,
    geraPreFaturamento: true, contratoFornecimento: false, vendaConsumo: false,
    naoContribuinteICMS: false, descontoSTIPI: false, isentoICMSST: false,
    expurgaICMSBase: false, utilizarB2B: false, qualidadeAssegurada: false,
    regimeEspecialIsencaoST: false,
    creditoVerificado: false, creditoAprovado: false,
    dataVerificacaoCredito: '', valorCredito: '',
    percentualDesconto: '', percDescontoAdicional: '',
    regimeApuracaoFiscal: 'Lucro Presumido', tipoAtividade: 'Outros',
    inscricaoSUFRAMA: '', bancoPadrao: '',
    bancoEnviaBoleto: false, enviaParaProtesto: false,
    clienteEstoqueTerceirista: false, restricaoEntregaCaminhao: false,
    frete: 'destinatario', transportadoraCodigo: '', transportadoraNome: '',
    executivosDeConta: [], clienteIndicadoPor: '', observacoes: '',
    accountExecutiveId: '',
};

const NEW_ADDRESS_MODEL = {
    tipo: '', logradouro: '', numero: '', complemento: '',
    cep: '', bairro: '', cidade: '', uf: '', pais: '', contacts: [],
    isImporter: false
};

const NEW_IMPORTER_ADDRESS_MODEL = {
    tipo: '',
    logradouro: '',
    numero: '',
    complemento: '',
    cep: '',
    bairro: '',
    cidade: '',
    uf: '',
    pais: '',
    contacts: [],
    isImporter: true,
    importerCompanyName: ''
};

const NEW_ADDR_CONTACT_MODEL = { nome: '', email: '', cargo: '', telefone: '' };

const selectStyles = {
    control: (base) => ({ ...base, borderColor: '#e9ecef', minHeight: '40px', fontSize: '1rem' }),
    multiValue: (base) => ({ ...base, backgroundColor: '#e2e8f0' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const isEqual = (obj1, obj2) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
};

const ClientsPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('clientes');
    const { toasts, addToast, removeToast } = useToast();

    // --- Core Data ---
    const { data: clients, loading: listLoading, addDocument, updateDocument, deleteDocument } = useFirestore(CLIENTS_COLLECTION, 'codigo');

    // --- Lazy Loaded Data ---
    const [shouldLoadExecutives, setShouldLoadExecutives] = useState(false);
    const { data: accountExecutives, loading: executivesLoading } = useFirestore(
        shouldLoadExecutives ? ACCOUNT_EXECUTIVES_COLLECTION : null,
        'nome'
    );

    const [shouldLoadParams, setShouldLoadParams] = useState(false);
    const { data: billingConditions, loading: billingLoading } = useFirestore(
        shouldLoadParams ? BILLING_CONDITIONS_COLLECTION : null,
        'name'
    );
    const { data: regions, loading: regionsLoading } = useFirestore(
        shouldLoadParams ? REGIONS_COLLECTION : null,
        'name'
    );
    const { data: rolesData, loading: rolesLoading } = useFirestore(
        shouldLoadParams ? ROLES_COLLECTION : null,
        'name'
    );
    const { data: paymentDocs, loading: paymentLoading } = useSubCollection(
        shouldLoadParams ? PAYMENT_DOC_PATH.parentCollection : null,
        shouldLoadParams ? PAYMENT_DOC_PATH.parentId : null,
        shouldLoadParams ? PAYMENT_DOC_PATH.subCollectionName : null,
        'nome'
    );
    const { data: addressTypesData, loading: addressTypesLoading } = useSubCollection(
        shouldLoadParams ? ADDRESS_TYPE_PATH.parentCollection : null,
        shouldLoadParams ? ADDRESS_TYPE_PATH.parentId : null,
        shouldLoadParams ? ADDRESS_TYPE_PATH.subCollectionName : null,
        'name'
    );

    const paramsLoading = billingLoading || regionsLoading || rolesLoading || paymentLoading || addressTypesLoading;

    // --- State ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(TABS.GERAL);
    const [searchTerm, setSearchTerm] = useState('');
    const [saveError, setSaveError] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_CLIENT_MODEL);

    // Addresses state
    const [addresses, setAddresses] = useState([]);
    const [newAddress, setNewAddress] = useState(NEW_ADDRESS_MODEL);
    const [isAddingAddress, setIsAddingAddress] = useState(false);
    const [importerAddresses, setImporterAddresses] = useState([]);
    const [newImporterAddress, setNewImporterAddress] = useState(NEW_IMPORTER_ADDRESS_MODEL);
    const [isAddingImporterAddress, setIsAddingImporterAddress] = useState(false);
    const [newAddrContact, setNewAddrContact] = useState(NEW_ADDR_CONTACT_MODEL);
    const [newExecutivo, setNewExecutivo] = useState('');

    // Address edit state
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [editingAddressIndex, setEditingAddressIndex] = useState(null);
    const [editingAddressType, setEditingAddressType] = useState('regular'); // 'regular' or 'importer'

    const originalClientRef = useRef(null);
    const isSubmitting = useRef(false);

    // --- Memoized Options ---
    const billingOptions = useMemo(() => (billingConditions || []).map(c => ({ value: c.id, label: `${c.name} (${c.days})` })), [billingConditions]);
    const regionOptions = useMemo(() => (regions || []).map(r => ({ value: r.id, label: r.name })), [regions]);
    const paymentOptions = useMemo(() => (paymentDocs || []).map(p => ({ value: p.id, label: p.nome })), [paymentDocs]);
    const roleOptions = useMemo(() => (rolesData || []).map(r => ({ value: r.name, label: r.name })), [rolesData]);
    const addressTypeOptions = useMemo(() => (addressTypesData || []).map(t => ({ value: t.name, label: t.name })), [addressTypesData]);

    const accountExecutiveOptions = useMemo(() => {
        if (!accountExecutives) return [];
        const activeExecutives = accountExecutives.filter(exec => exec.situacao === 'ativo');
        return activeExecutives.map(exec => ({
            value: exec.id,
            label: `${exec.nome}${exec.emailUsuario ? ` (${exec.emailUsuario})` : ''}`,
            comissaoPadrao: exec.comissaoPadrao || 0
        }));
    }, [accountExecutives]);

    // --- Effects for Lazy Loading ---
    useEffect(() => {
        if (isModalOpen) {
            if (!shouldLoadParams) setShouldLoadParams(true);
            if (!shouldLoadExecutives) setShouldLoadExecutives(true);
        }
    }, [isModalOpen, shouldLoadParams, shouldLoadExecutives]);

    useEffect(() => {
        if (!isModalOpen) {
            const timer = setTimeout(() => {
                setShouldLoadParams(false);
                setShouldLoadExecutives(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isModalOpen]);

    // --- Code Generation ---
    const generateNextCode = useCallback(async () => {
        const prefix = 'C';
        try {
            const nextNumber = await runTransaction(db, async (transaction) => {
                const clientsRef = collection(db, CLIENTS_COLLECTION);
                const q = query(clientsRef);
                const snapshot = await getDocs(q);
                let maxNumber = 0;
                snapshot.forEach(doc => {
                    const data = doc.data();
                    if (data.codigo?.startsWith(prefix)) {
                        const num = parseInt(data.codigo.slice(prefix.length), 10);
                        if (!isNaN(num) && num > maxNumber) maxNumber = num;
                    }
                });
                return maxNumber + 1;
            });
            return `${prefix}${nextNumber.toString().padStart(3, '0')}`;
        } catch (error) {
            console.error('Error generating code:', error);
            return getFallbackCode(prefix);
        }
    }, []);

    const getFallbackCode = useCallback((prefix) => {
        if (!clients || clients.length === 0) return `${prefix}001`;
        const nums = clients
            .filter(c => c.codigo?.startsWith(prefix))
            .map(c => parseInt(c.codigo.slice(prefix.length), 10))
            .filter(n => !isNaN(n));
        const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
        return `${prefix}${next.toString().padStart(3, '0')}`;
    }, [clients]);

    const checkDuplicateCode = useCallback(async (code, excludeId = null) => {
        try {
            const clientsRef = collection(db, CLIENTS_COLLECTION);
            const q = query(clientsRef, where('codigo', '==', code));
            const snapshot = await getDocs(q);
            if (excludeId) {
                return snapshot.docs.some(doc => doc.id !== excludeId);
            }
            return !snapshot.empty;
        } catch (error) {
            console.error('Error checking duplicate code:', error);
            return false;
        }
    }, []);

    // --- Handlers ---
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        setSaveError(null);
    };

    const handleSelectChange = (name, selectedOption) => {
        setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : '' }));
        setSaveError(null);
    };

    const handleAccountExecutiveChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            accountExecutiveId: selectedOption ? selectedOption.value : ''
        }));
        setSaveError(null);
    };

    const handleMultiCheckboxChange = (selectedOptions, allGroupOptions) => {
        const newValues = { ...formData };
        allGroupOptions.forEach(opt => { newValues[opt.value] = false; });
        if (selectedOptions) {
            selectedOptions.forEach(opt => { newValues[opt.value] = true; });
        }
        setFormData(newValues);
        setSaveError(null);
    };

    const resetForm = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setFormData(DEFAULT_CLIENT_MODEL);
        setAddresses([]);
        setImporterAddresses([]);
        setNewAddress(NEW_ADDRESS_MODEL);
        setNewImporterAddress(NEW_IMPORTER_ADDRESS_MODEL);
        setIsAddingAddress(false);
        setIsAddingImporterAddress(false);
        setNewAddrContact(NEW_ADDR_CONTACT_MODEL);
        setNewExecutivo('');
        setActiveTab(TABS.GERAL);
        setFormLoading(false);
        setSaveError(null);
        originalClientRef.current = null;
        isSubmitting.current = false;
        // Reset edit states
        setIsEditingAddress(false);
        setEditingAddressIndex(null);
        setEditingAddressType('regular');
    };

    const handleNewClick = async () => {
        if (!can.create) {
            addToast(t('pages.clients.noPermissionCreate'), 'error');
            return;
        }
        resetForm();
        setFormLoading(true);
        try {
            const newCode = await generateNextCode();
            setFormData({ ...DEFAULT_CLIENT_MODEL, codigo: newCode });
            setIsModalOpen(true);
        } catch (error) {
            console.error('Error generating client code:', error);
            addToast(t('pages.clients.createError'), 'error');
        } finally {
            setFormLoading(false);
        }
    };

    const handleEditClick = (client) => {
        if (!can.edit) {
            addToast(t('pages.clients.noPermissionEdit'), 'error');
            return;
        }
        resetForm();
        setEditingItem(client);
        const clientData = { ...DEFAULT_CLIENT_MODEL, ...client };
        setFormData(clientData);

        const allAddresses = client.addresses || [];
        const regular = allAddresses.filter(addr => !addr.isImporter);
        const importer = allAddresses.filter(addr => addr.isImporter);

        setAddresses(regular);
        setImporterAddresses(importer);
        originalClientRef.current = clientData;
        setIsModalOpen(true);
    };

    const hasChanges = useCallback(() => {
        if (!editingItem) return true;
        const allAddresses = [...addresses, ...importerAddresses];
        const currentData = { ...formData, addresses: allAddresses };
        const originalData = { ...originalClientRef.current, addresses: editingItem.addresses || [] };
        return !isEqual(currentData, originalData);
    }, [editingItem, formData, addresses, importerAddresses]);

    const handleSave = async (e) => {
        e.preventDefault();

        if (!editingItem && !can.create) {
            setSaveError(t('pages.clients.noPermissionCreate'));
            return;
        }
        if (editingItem && !can.edit) {
            setSaveError(t('pages.clients.noPermissionEdit'));
            return;
        }
        if (isSubmitting.current || formLoading) return;
        if (editingItem && !hasChanges()) {
            resetForm();
            return;
        }

        isSubmitting.current = true;
        setFormLoading(true);
        setSaveError(null);

        try {
            if (!editingItem || formData.codigo !== editingItem.codigo) {
                const isDuplicate = await checkDuplicateCode(formData.codigo, editingItem ? editingItem.id : null);
                if (isDuplicate) {
                    const newCode = await generateNextCode();
                    setFormData(prev => ({ ...prev, codigo: newCode }));
                    setSaveError(t('pages.clients.duplicateCodeGenerating'));
                    setFormLoading(false);
                    isSubmitting.current = false;
                    return;
                }
            }

            const allAddresses = [...addresses, ...importerAddresses];
            const dataToSave = { ...formData, addresses: allAddresses };

            Object.keys(dataToSave).forEach(key => {
                if (dataToSave[key] === undefined || dataToSave[key] === null) {
                    delete dataToSave[key];
                }
            });

            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, dataToSave);
                if (!result.success) {
                    throw new Error(result.error || t('pages.clients.updateError'));
                }
                addToast(t('pages.clients.updateSuccess'), 'success');
            } else {
                result = await addDocument(dataToSave);
                if (!result.success) {
                    throw new Error(result.error || t('pages.clients.createError'));
                }
                addToast(t('pages.clients.createSuccess'), 'success');
            }
            resetForm();
        } catch (error) {
            console.error('Erro ao salvar cliente:', error);
            setSaveError(error.message || t('pages.clients.saveError'));
            addToast(error.message || t('pages.clients.saveError'), 'error');
            setFormLoading(false);
            isSubmitting.current = false;
        }
    };

    // --- Address Handlers ---
    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setNewAddress(prev => ({ ...prev, [name]: value }));
    };

    const handleAddrContactChange = (e) => {
        const { name, value } = e.target;
        setNewAddrContact(prev => ({ ...prev, [name]: value }));
    };

    const handleAddAddrContact = (isImporter = false) => {
        if (!newAddrContact.nome.trim()) {
            addToast(t('pages.clients.form.contactNameRequired'), 'error');
            return;
        }
        if (isImporter) {
            setNewImporterAddress(prev => ({ ...prev, contacts: [...prev.contacts, { ...newAddrContact }] }));
        } else {
            setNewAddress(prev => ({ ...prev, contacts: [...prev.contacts, { ...newAddrContact }] }));
        }
        setNewAddrContact(NEW_ADDR_CONTACT_MODEL);
    };

    const handleRemoveAddrContact = (index, isImporter = false) => {
        if (isImporter) {
            setNewImporterAddress(prev => ({ ...prev, contacts: prev.contacts.filter((_, i) => i !== index) }));
        } else {
            setNewAddress(prev => ({ ...prev, contacts: prev.contacts.filter((_, i) => i !== index) }));
        }
    };

    const handleAddAddress = () => {
        if (!newAddress.logradouro.trim()) {
            addToast(t('pages.clients.form.streetRequired'), 'error');
            return;
        }
        setAddresses(prev => [...prev, { ...newAddress, isImporter: false }]);
        setNewAddress(NEW_ADDRESS_MODEL);
        setNewAddrContact(NEW_ADDR_CONTACT_MODEL);
        setIsAddingAddress(false);
        addToast(t('pages.clients.form.addressAdded'), 'success');
    };

    const handleRemoveAddress = (index) => {
        setAddresses(prev => prev.filter((_, i) => i !== index));
    };

    const handleAddImporterAddress = () => {
        if (!newImporterAddress.logradouro.trim()) {
            addToast(t('pages.clients.form.streetRequired'), 'error');
            return;
        }
        setImporterAddresses(prev => [...prev, { ...newImporterAddress, isImporter: true }]);
        setNewImporterAddress(NEW_IMPORTER_ADDRESS_MODEL);
        setNewAddrContact(NEW_ADDR_CONTACT_MODEL);
        setIsAddingImporterAddress(false);
        addToast(t('pages.clients.form.importerAddressAdded'), 'success');
    };

    const handleRemoveImporterAddress = (index) => {
        setImporterAddresses(prev => prev.filter((_, i) => i !== index));
    };

    // --- Address Edit Handlers ---
    const handleEditAddress = (index, isImporter = false) => {
        const addressList = isImporter ? importerAddresses : addresses;
        const address = addressList[index];

        if (isImporter) {
            setNewImporterAddress({ ...address });
            setIsAddingImporterAddress(true);
        } else {
            setNewAddress({ ...address });
            setIsAddingAddress(true);
        }

        // Store the index and type for update instead of add
        setEditingAddressIndex(index);
        setEditingAddressType(isImporter ? 'importer' : 'regular');
        setIsEditingAddress(true);
    };

    const handleUpdateAddress = (isImporter = false) => {
        const currentAddress = isImporter ? newImporterAddress : newAddress;

        if (!currentAddress.logradouro.trim()) {
            addToast(t('pages.clients.form.streetRequired'), 'error');
            return;
        }

        const index = editingAddressIndex;

        if (isImporter) {
            setImporterAddresses(prev => prev.map((addr, i) =>
                i === index ? { ...currentAddress, isImporter: true } : addr
            ));
            setIsAddingImporterAddress(false);
            setNewImporterAddress(NEW_IMPORTER_ADDRESS_MODEL);
        } else {
            setAddresses(prev => prev.map((addr, i) =>
                i === index ? { ...currentAddress, isImporter: false } : addr
            ));
            setIsAddingAddress(false);
            setNewAddress(NEW_ADDRESS_MODEL);
        }

        setEditingAddressIndex(null);
        setIsEditingAddress(false);
        setNewAddrContact(NEW_ADDR_CONTACT_MODEL);
        addToast(t('pages.clients.form.addressUpdated'), 'success');
    };

    const handleCancelAddressEdit = (isImporter = false) => {
        if (isImporter) {
            setIsAddingImporterAddress(false);
            setNewImporterAddress(NEW_IMPORTER_ADDRESS_MODEL);
        } else {
            setIsAddingAddress(false);
            setNewAddress(NEW_ADDRESS_MODEL);
        }
        setEditingAddressIndex(null);
        setIsEditingAddress(false);
        setNewAddrContact(NEW_ADDR_CONTACT_MODEL);
    };

    // --- Executive Handlers ---
    const handleAddExecutivo = () => {
        if (!newExecutivo.trim()) return;
        setFormData(prev => ({
            ...prev, executivosDeConta: [...prev.executivosDeConta, { nome: newExecutivo.trim() }]
        }));
        setNewExecutivo('');
    };

    const handleRemoveExecutivo = (index) => {
        setFormData(prev => ({
            ...prev, executivosDeConta: prev.executivosDeConta.filter((_, i) => i !== index)
        }));
    };

    const handleDeleteClick = async (client) => {
        if (!can.delete) {
            addToast(t('pages.clients.noPermissionDelete'), 'error');
            return;
        }
        if (window.confirm(t('pages.clients.deleteConfirm', { name: client.razaoSocial || client.codigo }))) {
            try {
                const result = await deleteDocument(client.id);
                if (!result.success) {
                    addToast(`${t('pages.clients.deleteError')}: ${result.error || t('common.unknownError')}`, 'error');
                } else {
                    addToast(t('pages.clients.deleteSuccess'), 'success');
                }
            } catch (error) {
                console.error('Erro ao excluir cliente:', error);
                addToast(`${t('pages.clients.deleteError')}: ${error.message}`, 'error');
            }
        }
    };

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
    }, []);

    // --- Filtering and Pagination ---
    const filteredClients = useMemo(() => {
        if (!searchTerm.trim()) return clients || [];
        const lowerTerm = searchTerm.toLowerCase().trim();
        return (clients || []).filter(c =>
            c.razaoSocial?.toLowerCase().includes(lowerTerm) ||
            c.nomeFantasia?.toLowerCase().includes(lowerTerm) ||
            c.cnpjCpf?.includes(lowerTerm) ||
            c.codigo?.toLowerCase().includes(lowerTerm)
        );
    }, [clients, searchTerm]);

    const {
        currentPage,
        totalPages,
        currentItems: currentClients,
        goToPage,
        resetPage,
        setCurrentPage
    } = usePagination(filteredClients, ITEMS_PER_PAGE);

    // Reset page when search changes
    useEffect(() => {
        resetPage();
    }, [searchTerm, resetPage]);

    // --- Render Functions ---
    const renderAddressCardContent = (addr) => (
        <div className={styles.addressCardContent}>
            {addr.isImporter && addr.importerCompanyName && (
                <p><strong>{t('pages.clients.form.importerCompanyName')}:</strong> {addr.importerCompanyName}</p>
            )}
            <p><strong>{t('pages.clients.form.street')}:</strong> {addr.logradouro}, {addr.numero}</p>
            {addr.complemento && <p><strong>{t('pages.clients.form.complement')}:</strong> {addr.complemento}</p>}
            <p><strong>{t('pages.clients.form.neighborhood')}:</strong> {addr.bairro}</p>
            <p><strong>{t('pages.clients.form.city')}/{t('pages.clients.form.state')}:</strong> {addr.cidade} - {addr.uf}</p>
            <p><strong>{t('pages.clients.form.zipCode')}:</strong> {addr.cep}</p>
            {/* ADDED: Country field display */}
            {addr.pais && <p><strong>{t('pages.clients.form.country')}:</strong> {addr.pais}</p>}
            {addr.contacts?.length > 0 && (
                <div className={styles.addressContacts}>
                    <strong>{t('pages.clients.form.contacts')}:</strong>
                    <ul>
                        {addr.contacts.map((c, ci) => (
                            <li key={ci}>
                                {c.nome}
                                {c.cargo && ` - ${c.cargo}`}
                                {c.email && ` (${c.email})`}
                                {c.telefone && ` - ${c.telefone}`}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );

    const renderAddressForm = (isImporter = false) => {
        const currentAddress = isImporter ? newImporterAddress : newAddress;
        const setCurrentAddress = isImporter ? setNewImporterAddress : setNewAddress;
        const isEditing = isEditingAddress && editingAddressIndex !== null &&
            (isImporter ? editingAddressType === 'importer' : editingAddressType === 'regular');

        const handleChange = isImporter ? (e) => {
            const { name, value } = e.target;
            setCurrentAddress(prev => ({ ...prev, [name]: value }));
        } : handleAddressChange;

        const handleSave = isImporter ? () => handleUpdateAddress(true) : handleAddAddress;
        const handleCancel = () => handleCancelAddressEdit(isImporter);
        const handleAddContact = () => handleAddAddrContact(isImporter);
        const handleRemoveContact = (index) => handleRemoveAddrContact(index, isImporter);

        const saveButtonText = isEditing
            ? (isImporter ? t('pages.clients.form.updateImporterAddress') : t('pages.clients.form.updateAddress'))
            : (isImporter ? t('pages.clients.form.addImporterAddress') : t('pages.clients.form.addAddress'));

        return (
            <fieldset className={styles.fieldset} style={{ marginTop: '15px', backgroundColor: isImporter ? '#f0fff4' : '#fdfdfd' }}>
                <legend>{isEditing
                    ? (isImporter ? t('pages.clients.form.editImporterAddress') : t('pages.clients.form.editAddress'))
                    : (isImporter ? t('pages.clients.form.newImporterAddress') : t('pages.clients.form.newAddress'))
                }</legend>

                {isImporter && (
                    <div className={styles.formRow}>
                        <div className={styles.formGroup} style={{ flex: 1 }}>
                            <label>{t('pages.clients.form.importerCompanyName')} *</label>
                            <input
                                className={styles.input}
                                name="importerCompanyName"
                                value={currentAddress.importerCompanyName || ''}
                                onChange={handleChange}
                                placeholder={t('pages.clients.form.importerCompanyNamePlaceholder')}
                                required
                            />
                        </div>
                    </div>
                )}

                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>{t('pages.clients.form.addressType')} *</label>
                        <Select
                            styles={selectStyles}
                            options={addressTypeOptions}
                            value={addressTypeOptions.find(o => o.value === currentAddress.tipo) || null}
                            onChange={(opt) => setCurrentAddress(prev => ({ ...prev, tipo: opt ? opt.value : '' }))}
                            placeholder={t('common.select') + '...'}
                            isClearable
                            isLoading={addressTypesLoading}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>{t('pages.clients.form.zipCode')}</label>
                        <input className={styles.input} name="cep" value={currentAddress.cep} onChange={handleChange} />
                    </div>
                </div>
                <div className={styles.formRow}>
                    <div className={styles.formGroup} style={{ flex: 3 }}>
                        <label>{t('pages.clients.form.street')} *</label>
                        <input className={styles.input} name="logradouro" value={currentAddress.logradouro} onChange={handleChange} />
                    </div>
                    <div className={styles.formGroup} style={{ flex: 1 }}>
                        <label>{t('pages.clients.form.number')}</label>
                        <input className={styles.input} name="numero" value={currentAddress.numero} onChange={handleChange} />
                    </div>
                    <div className={styles.formGroup} style={{ flex: 2 }}>
                        <label>{t('pages.clients.form.complement')}</label>
                        <input className={styles.input} name="complemento" value={currentAddress.complemento} onChange={handleChange} />
                    </div>
                </div>
                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>{t('pages.clients.form.neighborhood')}</label>
                        <input className={styles.input} name="bairro" value={currentAddress.bairro} onChange={handleChange} />
                    </div>
                    <div className={styles.formGroup}>
                        <label>{t('pages.clients.form.city')}</label>
                        <input className={styles.input} name="cidade" value={currentAddress.cidade} onChange={handleChange} />
                    </div>
                    <div className={styles.formGroup}>
                        <label>{t('pages.clients.form.state')}</label>
                        <input className={styles.input} name="uf" value={currentAddress.uf} onChange={handleChange} />
                    </div>
                </div>
                {/* ADDED: Country field */}
                <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                        <label>{t('pages.clients.form.country')}</label>
                        <input
                            className={styles.input}
                            name="pais"
                            value={currentAddress.pais || ''}
                            onChange={handleChange}
                            placeholder={t('pages.clients.form.countryPlaceholder')}
                        />
                    </div>
                </div>

                <fieldset className={styles.fieldset} style={{ marginTop: '20px' }}>
                    <legend>{t('pages.clients.form.contacts')}</legend>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>{t('pages.clients.form.contactName')}</label>
                            <input className={styles.input} name="nome" value={newAddrContact.nome} onChange={handleAddrContactChange} placeholder={t('pages.clients.form.contactNamePlaceholder')} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.clients.form.contactEmail')}</label>
                            <input className={styles.input} type="email" name="email" value={newAddrContact.email} onChange={handleAddrContactChange} />
                        </div>
                        <div className={styles.formGroup} style={{ minWidth: '200px' }}>
                            <label>{t('pages.clients.form.contactPosition')}</label>
                            <Select
                                styles={selectStyles}
                                options={roleOptions}
                                value={roleOptions.find(o => o.value === newAddrContact.cargo) || null}
                                onChange={(opt) => setNewAddrContact(prev => ({ ...prev, cargo: opt ? opt.value : '' }))}
                                placeholder={t('pages.clients.form.contactPositionPlaceholder')}
                                isClearable
                                isLoading={rolesLoading}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>{t('pages.clients.form.contactPhone')}</label>
                            <input className={styles.input} type="text" name="telefone" value={newAddrContact.telefone} onChange={handleAddrContactChange} placeholder={t('pages.clients.form.contactPhonePlaceholder')} />
                        </div>
                    </div>
                    {can.edit && (
                        <button type="button" onClick={handleAddContact} className={styles.addButton} style={{ marginTop: '10px' }}>
                            + {t('pages.clients.form.addContact')}
                        </button>
                    )}

                    {currentAddress.contacts.length > 0 && (
                        <div className={styles.tableContainer} style={{ marginTop: '15px' }}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t('pages.clients.form.contactName')}</th>
                                        <th>{t('pages.clients.form.contactEmail')}</th>
                                        <th>{t('pages.clients.form.contactPosition')}</th>
                                        <th>{t('pages.clients.form.contactPhone')}</th>
                                        <th>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentAddress.contacts.map((c, i) => (
                                        <tr key={i}>
                                            <td>{c.nome}</td>
                                            <td>{c.email}</td>
                                            <td>{c.cargo}</td>
                                            <td>{c.telefone}</td>
                                            <td>
                                                <button type="button" onClick={() => handleRemoveContact(i)} className={`${styles.actionButton} ${styles.deleteButton}`}>
                                                    {t('common.delete')}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </fieldset>

                <div className={styles.formActions} style={{ borderTop: '1px solid #ddd', marginTop: '20px', paddingTop: '15px' }}>
                    <button type="button" onClick={handleCancel} className={styles.cancelButton}>
                        {t('pages.clients.form.cancelAddress')}
                    </button>
                    <button type="button" onClick={handleSave} className={styles.addButton}>
                        {saveButtonText}
                    </button>
                </div>
            </fieldset>
        );
    };

    // --- Table Render ---
    const renderTable = () => {
        if (listLoading) {
            return <TableSkeleton columns={7} rows={ITEMS_PER_PAGE} />;
        }

        if (currentClients.length === 0) {
            return (
                <div className={styles.tableContainer}>
                    <EmptyState
                        icon="🏢"
                        title={searchTerm ? t('pages.clients.table.emptySearch') : t('pages.clients.table.empty')}
                        message={searchTerm ? t('pages.clients.search.noResults') : t('pages.clients.table.emptyMessage')}
                        actionText={can.create ? t('pages.clients.newClient') : undefined}
                        onAction={can.create ? handleNewClick : undefined}
                    />
                </div>
            );
        }

        return (
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>{t('pages.clients.table.code')}</th>
                        <th>{t('pages.clients.table.companyName')}</th>
                        <th>{t('pages.clients.table.tradeName')}</th>
                        <th>{t('pages.clients.table.document')}</th>
                        <th>{t('pages.clients.table.country')}</th>
                        <th>{t('pages.clients.table.situation')}</th>
                        <th style={{ width: '150px' }}>{t('common.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {currentClients.map(client => (
                        <tr key={client.id}>
                            <td>{client.codigo || '-'}</td>
                            <td>{client.razaoSocial || '-'}</td>
                            <td>{client.nomeFantasia || '-'}</td>
                            <td>{client.cnpjCpf || '-'}</td>
                            <td>{countryOptions.find(o => o.value === client.pais)?.label || client.pais || '-'}</td>
                            <td>{client.situacao || '-'}</td>
                            <td>
                                <div className={styles.actionsContainer}>
                                    {can.edit && (
                                        <button onClick={() => handleEditClick(client)} className={styles.actionButton} disabled={listLoading}>
                                            <FiEdit />
                                        </button>
                                    )}
                                    {can.delete && (
                                        <button
                                            onClick={() => handleDeleteClick(client)}
                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                            disabled={listLoading}
                                        >
                                            <FiTrash2 />
                                        </button>
                                    )}
                                    {!can.edit && !can.delete && (
                                        <span className={styles.textMuted}>{t('common.noActionsAvailable')}</span>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    // --- Modal Content ---
    const renderModalContent = () => {
        const selectedCountry = countryOptions.find(o => o.value === formData.pais) || null;
        const selectedBilling = billingOptions.find(o => o.value === formData.condicaoFaturamentoId) || null;
        const selectedPayment = paymentOptions.find(o => o.value === formData.formaPagamentoId) || null;
        const selectedRegion = regionOptions.find(o => o.value === formData.regiaoId) || null;
        const selectedDiaSemana = DIAS_SEMANA_OPTIONS.find(o => o.value === formData.diaSemanaPagamento) || null;
        const selectedTipo = TIPO_CLIENTE_OPTIONS.find(o => o.value === formData.tipo) || null;
        const selectedAccountExecutive = accountExecutiveOptions.find(o => o.value === formData.accountExecutiveId) || null;

        const isFormDisabled = !can.create && !can.edit;

        return (
            <div className={styles.formContent}>
                {(paramsLoading || executivesLoading) && (
                    <div className={styles.infoBox} style={{ margin: '0 32px 20px 32px' }}>
                        <span className={styles.alertIcon}>🔄</span>
                        {t('common.loading')}
                    </div>
                )}

                <div className={styles.tabsContainer}>
                    <div className={styles.tabsHeader}>
                        {Object.values(TABS).map(tab => {
                            const tabKey = tab === TABS.GERAL ? 'general' : tab === TABS.FISCAL ? 'fiscal' : 'addresses';
                            return (
                                <button
                                    key={tab}
                                    type="button"
                                    className={`${styles.tabButton} ${activeTab === tab ? styles.activeTab : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                    disabled={formLoading}
                                >
                                    {tab === TABS.GERAL && <FaClipboard />}
                                    {tab === TABS.FISCAL && <FaMoneyCheck />}
                                    {tab === TABS.ENDERECOS && <FiMapPin />}
                                    {t(`pages.clients.tabs.${tabKey}`)}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {activeTab === TABS.GERAL && (
                    <>
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><HiOutlineIdentification /></div>
                                <h3 className={styles.sectionTitle}>{t('pages.clients.form.identification')}</h3>
                                <span className={styles.sectionBadge}>{t('common.required')}</span>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.code')}</label>
                                    <input
                                        className={styles.input}
                                        name="codigo"
                                        value={formData.codigo}
                                        onChange={handleChange}
                                        disabled
                                    />
                                    <small className={styles.fieldHint}>{t('pages.clients.form.codeHint')}</small>
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.situation')}</label>
                                    <select
                                        className={styles.select}
                                        name="situacao"
                                        value={formData.situacao}
                                        onChange={handleChange}
                                        disabled={formLoading || isFormDisabled}
                                    >
                                        <option value="Ativo">{t('pages.clients.table.active')}</option>
                                        <option value="Inativo">{t('pages.clients.table.inactive')}</option>
                                    </select>
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.documentType')}</label>
                                    <Select
                                        styles={selectStyles}
                                        options={TIPO_DOCUMENTO_OPTIONS}
                                        value={TIPO_DOCUMENTO_OPTIONS.find(o => o.value === formData.tipoPessoa)}
                                        onChange={(opt) => handleSelectChange('tipoPessoa', opt)}
                                        isDisabled={formLoading || isFormDisabled}
                                        placeholder={t('common.select') + '...'}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.document')}</label>
                                    <input
                                        className={styles.input}
                                        name="cnpjCpf"
                                        value={formData.cnpjCpf}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.documentPlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.stateRegistration')}</label>
                                    <input
                                        className={styles.input}
                                        name="inscricaoEstadual"
                                        value={formData.inscricaoEstadual}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.stateRegistrationPlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.companyName')} <span className={styles.required}>*</span></label>
                                    <input
                                        className={styles.input}
                                        name="razaoSocial"
                                        value={formData.razaoSocial}
                                        onChange={handleChange}
                                        required
                                        placeholder={t('pages.clients.form.companyNamePlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.tradeName')}</label>
                                    <input
                                        className={styles.input}
                                        name="nomeFantasia"
                                        value={formData.nomeFantasia}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.tradeNamePlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.cnae')}</label>
                                    <input
                                        className={styles.input}
                                        name="cnae"
                                        value={formData.cnae}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.cnaePlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FaBuilding /></div>
                                <h3 className={styles.sectionTitle}>{t('pages.clients.form.commercialData')}</h3>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.region')}</label>
                                    <Select
                                        styles={selectStyles}
                                        options={regionOptions}
                                        value={selectedRegion}
                                        onChange={(opt) => handleSelectChange('regiaoId', opt)}
                                        placeholder={t('pages.clients.form.regionPlaceholder')}
                                        isClearable
                                        isDisabled={formLoading || regionsLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.clientType')}</label>
                                    <Select
                                        styles={selectStyles}
                                        options={TIPO_CLIENTE_OPTIONS}
                                        value={selectedTipo}
                                        onChange={(opt) => handleSelectChange('tipo', opt)}
                                        isDisabled={formLoading || isFormDisabled}
                                        placeholder={t('common.select') + '...'}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.clientGroup')}</label>
                                    <input
                                        className={styles.input}
                                        name="grupoClientes"
                                        value={formData.grupoClientes}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.clientGroupPlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.paymentMethod')}</label>
                                    <Select
                                        styles={selectStyles}
                                        options={paymentOptions}
                                        value={selectedPayment}
                                        onChange={(opt) => handleSelectChange('formaPagamentoId', opt)}
                                        placeholder={t('common.select') + '...'}
                                        isClearable
                                        isDisabled={formLoading || paymentLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.billingCondition')}</label>
                                    <Select
                                        styles={selectStyles}
                                        options={billingOptions}
                                        value={selectedBilling}
                                        onChange={(opt) => handleSelectChange('condicaoFaturamentoId', opt)}
                                        placeholder={t('common.select') + '...'}
                                        isClearable
                                        isDisabled={formLoading || billingLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.paymentDay')}</label>
                                    <Select
                                        styles={selectStyles}
                                        options={DIAS_SEMANA_OPTIONS}
                                        value={selectedDiaSemana}
                                        onChange={(opt) => handleSelectChange('diaSemanaPagamento', opt)}
                                        isClearable
                                        isDisabled={formLoading || isFormDisabled}
                                        placeholder={t('common.select') + '...'}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.country')}</label>
                                    <Select
                                        styles={selectStyles}
                                        options={countryOptions}
                                        value={selectedCountry}
                                        onChange={(opt) => handleSelectChange('pais', opt)}
                                        placeholder={t('pages.clients.form.countryPlaceholder')}
                                        isDisabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.website')}</label>
                                    <input
                                        className={styles.input}
                                        name="website"
                                        value={formData.website}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.websitePlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.charges')}</label>
                                    <input
                                        className={styles.input}
                                        type="number"
                                        name="encargos"
                                        value={formData.encargos}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.chargesPlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.companyTime')}</label>
                                    <input
                                        className={styles.input}
                                        name="tempoEmpresa"
                                        value={formData.tempoEmpresa}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.companyTimePlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                            </div>

                            <div className={styles.formSection} style={{ marginTop: '20px', overflow: 'visible' }}>
                                <div className={styles.sectionHeader}>
                                    <div className={styles.sectionIcon}><FaUserTie /></div>
                                    <h3 className={styles.sectionTitle}>{t('pages.clients.form.executive')}</h3>
                                </div>
                                <div className={styles.formGrid} style={{ overflow: 'visible' }}>
                                    <div className={styles.inputGroup} style={{ position: 'relative', zIndex: 10, overflow: 'visible' }}>
                                        <label>{t('pages.clients.form.executive')}</label>
                                        <Select
                                            {...selectStyles}
                                            styles={{
                                                ...selectStyles,
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                menu: (base) => ({ ...base, zIndex: 9999 })
                                            }}
                                            options={accountExecutiveOptions}
                                            value={selectedAccountExecutive}
                                            onChange={handleAccountExecutiveChange}
                                            placeholder={executivesLoading ? t('common.loading') : t('pages.clients.form.executivePlaceholder')}
                                            isClearable
                                            isDisabled={formLoading || executivesLoading || isFormDisabled}
                                            noOptionsMessage={() => t('pages.clients.form.noExecutiveFound')}
                                            menuPortalTarget={document.body}
                                        />
                                        <small className={styles.fieldHint}>{t('pages.clients.form.executiveHint')}</small>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.dollarType')}</label>
                                    <div className={styles.radioGroup}>
                                        <label className={styles.radioLabel}>
                                            <input type="radio" name="tipoDolar" value="medio" checked={formData.tipoDolar === "medio"} onChange={handleChange} disabled={formLoading || isFormDisabled} />
                                            <span>{t('pages.clients.form.dollarMedium')}</span>
                                        </label>
                                        <label className={styles.radioLabel}>
                                            <input type="radio" name="tipoDolar" value="negociado" checked={formData.tipoDolar === "negociado"} onChange={handleChange} disabled={formLoading || isFormDisabled} />
                                            <span>{t('pages.clients.form.dollarNegotiated')}</span>
                                        </label>
                                        <label className={styles.radioLabel}>
                                            <input type="radio" name="tipoDolar" value="dia" checked={formData.tipoDolar === "dia"} onChange={handleChange} disabled={formLoading || isFormDisabled} />
                                            <span>{t('pages.clients.form.dollarDaily')}</span>
                                        </label>
                                    </div>
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.dollarValue')}</label>
                                    <input
                                        className={styles.input}
                                        type="number"
                                        step="0.0001"
                                        name="valorDolar"
                                        value={formData.valorDolar}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.dollarValuePlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.lastBilledDollar')}</label>
                                    <input
                                        className={styles.input}
                                        type="number"
                                        step="0.0001"
                                        name="ultimoDolarFaturado"
                                        value={formData.ultimoDolarFaturado}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.lastBilledDollarPlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}>📄</div>
                                <h3 className={styles.sectionTitle}>{t('pages.clients.form.taxSettings')}</h3>
                            </div>
                            <div className={styles.inputGroup} style={{ padding: '0 20px 20px 20px' }}>
                                <label>{t('pages.clients.form.taxAttributes')}</label>
                                <Select
                                    styles={selectStyles}
                                    isMulti
                                    options={FISCAL_OPTIONS}
                                    value={FISCAL_OPTIONS.filter(opt => formData[opt.value])}
                                    onChange={(selected) => handleMultiCheckboxChange(selected, FISCAL_OPTIONS)}
                                    placeholder={t('pages.clients.form.taxAttributesPlaceholder')}
                                    isDisabled={formLoading || isFormDisabled}
                                    closeMenuOnSelect={false}
                                />
                            </div>
                            <div style={{ padding: '0 20px 20px 20px' }}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.irWithholding')}</label>
                                    <input
                                        className={styles.input}
                                        type="number"
                                        name="retencaoIR"
                                        value={formData.retencaoIR}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.irWithholdingPlaceholder')}
                                        style={{ maxWidth: '200px' }}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === TABS.FISCAL && (
                    <>
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FaCreditCard /></div>
                                <h3 className={styles.sectionTitle}>{t('pages.clients.form.credit')}</h3>
                            </div>
                            <div className={styles.checkboxGrid} style={{ padding: '20px' }}>
                                <label className={styles.checkboxLabel}>
                                    <input type="checkbox" name="creditoVerificado" checked={formData.creditoVerificado} onChange={handleChange} disabled={formLoading || isFormDisabled} />
                                    <span className={styles.checkboxText}>{t('pages.clients.form.creditVerified')}</span>
                                </label>
                                <label className={styles.checkboxLabel}>
                                    <input type="checkbox" name="creditoAprovado" checked={formData.creditoAprovado} onChange={handleChange} disabled={formLoading || isFormDisabled} />
                                    <span className={styles.checkboxText}>{t('pages.clients.form.creditApproved')}</span>
                                </label>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.verificationDate')}</label>
                                    <input
                                        className={styles.input}
                                        type="date"
                                        name="dataVerificacaoCredito"
                                        value={formData.dataVerificacaoCredito}
                                        onChange={handleChange}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.creditValue')}</label>
                                    <input
                                        className={styles.input}
                                        type="number"
                                        name="valorCredito"
                                        value={formData.valorCredito}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.creditValuePlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.discount')}</label>
                                    <input
                                        className={styles.input}
                                        type="number"
                                        name="percentualDesconto"
                                        value={formData.percentualDesconto}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.discountPlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.additionalDiscount')}</label>
                                    <input
                                        className={styles.input}
                                        type="number"
                                        name="percDescontoAdicional"
                                        value={formData.percDescontoAdicional}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.additionalDiscountPlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FaBalanceScale /></div>
                                <h3 className={styles.sectionTitle}>{t('pages.clients.form.taxRegimeAndActivity')}</h3>
                            </div>
                            <div className={styles.radioGrid} style={{ padding: '20px' }}>
                                <label className={styles.sectionLabel}>{t('pages.clients.form.taxRegime')}</label>
                                <div className={styles.radioGroup}>
                                    {REGIME_APURACAO_OPTIONS.map(opt => (
                                        <label key={opt} className={styles.radioLabel}>
                                            <input type="radio" name="regimeApuracaoFiscal" value={opt} checked={formData.regimeApuracaoFiscal === opt} onChange={handleChange} disabled={formLoading || isFormDisabled} />
                                            <span>{opt}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.radioGrid} style={{ padding: '0 20px 20px 20px' }}>
                                <label className={styles.sectionLabel}>{t('pages.clients.form.activityType')}</label>
                                <div className={styles.radioGroup}>
                                    <label className={styles.radioLabel}>
                                        <input type="radio" name="tipoAtividade" value="Industrial ou Equiparado a Industrial" checked={formData.tipoAtividade === "Industrial ou Equiparado a Industrial"} onChange={handleChange} disabled={formLoading || isFormDisabled} />
                                        <span>{t('pages.clients.form.activityIndustrial')}</span>
                                    </label>
                                    <label className={styles.radioLabel}>
                                        <input type="radio" name="tipoAtividade" value="Outros" checked={formData.tipoAtividade === "Outros"} onChange={handleChange} disabled={formLoading || isFormDisabled} />
                                        <span>{t('pages.clients.form.activityOthers')}</span>
                                    </label>
                                </div>
                            </div>
                            <div style={{ padding: '0 20px 20px 20px' }}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.suframaRegistration')}</label>
                                    <input
                                        className={styles.input}
                                        name="inscricaoSUFRAMA"
                                        value={formData.inscricaoSUFRAMA}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.suframaPlaceholder')}
                                        style={{ maxWidth: '300px' }}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><MdAccountBalance /></div>
                                <h3 className={styles.sectionTitle}>{t('pages.clients.form.bankAndLogistics')}</h3>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.defaultBank')}</label>
                                    <input
                                        className={styles.input}
                                        name="bancoPadrao"
                                        value={formData.bancoPadrao}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.defaultBankPlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.bankOptions')}</label>
                                    <Select
                                        styles={selectStyles}
                                        isMulti
                                        options={BANK_OPTIONS}
                                        value={BANK_OPTIONS.filter(opt => formData[opt.value])}
                                        onChange={(selected) => handleMultiCheckboxChange(selected, BANK_OPTIONS)}
                                        placeholder={t('pages.clients.form.bankOptionsPlaceholder')}
                                        isDisabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.logisticsOptions')}</label>
                                    <Select
                                        styles={selectStyles}
                                        isMulti
                                        options={LOGISTICS_OPTIONS}
                                        value={LOGISTICS_OPTIONS.filter(opt => formData[opt.value])}
                                        onChange={(selected) => handleMultiCheckboxChange(selected, LOGISTICS_OPTIONS)}
                                        placeholder={t('pages.clients.form.logisticsOptionsPlaceholder')}
                                        isDisabled={formLoading || isFormDisabled}
                                    />
                                </div>
                            </div>
                            <div className={styles.radioGrid} style={{ padding: '20px' }}>
                                <label className={styles.sectionLabel}>{t('pages.clients.form.freightType')}</label>
                                <div className={styles.radioGroup}>
                                    <label className={styles.radioLabel}>
                                        <input type="radio" name="frete" value="emitente" checked={formData.frete === "emitente"} onChange={handleChange} disabled={formLoading || isFormDisabled} />
                                        <span>{t('pages.clients.form.freightEmitente')}</span>
                                    </label>
                                    <label className={styles.radioLabel}>
                                        <input type="radio" name="frete" value="destinatario" checked={formData.frete === "destinatario"} onChange={handleChange} disabled={formLoading || isFormDisabled} />
                                        <span>{t('pages.clients.form.freightDestinatario')}</span>
                                    </label>
                                </div>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.carrierCode')}</label>
                                    <input
                                        className={styles.input}
                                        name="transportadoraCodigo"
                                        value={formData.transportadoraCodigo}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.carrierCodePlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                    <label>{t('pages.clients.form.carrierName')}</label>
                                    <input
                                        className={styles.input}
                                        name="transportadoraNome"
                                        value={formData.transportadoraNome}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.carrierNamePlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FaPeopleGroup /></div>
                                <h3 className={styles.sectionTitle}>{t('pages.clients.form.multipleExecutives')}</h3>
                                <small className={styles.fieldHint}>{t('pages.clients.form.multipleExecutivesHint')}</small>
                            </div>
                            <div className={styles.formRow} style={{ padding: '20px', gap: '16px', alignItems: 'flex-end' }}>
                                <div className={styles.inputGroup} style={{ flex: 1 }}>
                                    <label>{t('pages.clients.form.executiveName')}</label>
                                    <input
                                        className={styles.input}
                                        value={newExecutivo}
                                        onChange={(e) => setNewExecutivo(e.target.value)}
                                        placeholder={t('pages.clients.form.executiveNamePlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup} style={{ flex: 'none' }}>
                                    <button type="button" onClick={handleAddExecutivo} className={styles.addButton} disabled={formLoading}>
                                        + {t('pages.clients.form.addExecutive')}
                                    </button>
                                </div>
                            </div>
                            {formData.executivosDeConta.length > 0 && (
                                <div className={styles.tableContainer} style={{ margin: '0 20px 20px 20px' }}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr><th>{t('pages.clients.form.executiveName')}</th><th style={{ width: '100px' }}>{t('common.actions')}</th></tr>
                                        </thead>
                                        <tbody>
                                            {formData.executivosDeConta.map((exec, i) => (
                                                <tr key={i}>
                                                    <td>{exec.nome}</td>
                                                    <td>
                                                        <button type="button" onClick={() => handleRemoveExecutivo(i)} className={`${styles.actionButton} ${styles.deleteButton}`} disabled={formLoading}>
                                                            {t('common.delete')}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}>📝</div>
                                <h3 className={styles.sectionTitle}>{t('pages.clients.form.referralAndNotes')}</h3>
                            </div>
                            <div style={{ padding: '20px' }}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.clients.form.referredBy')}</label>
                                    <input
                                        className={styles.input}
                                        name="clienteIndicadoPor"
                                        value={formData.clienteIndicadoPor}
                                        onChange={handleChange}
                                        placeholder={t('pages.clients.form.referredByPlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                                <div className={styles.inputGroup} style={{ marginTop: '16px' }}>
                                    <label>{t('pages.clients.form.observations')}</label>
                                    <textarea
                                        className={styles.textarea}
                                        name="observacoes"
                                        value={formData.observacoes}
                                        onChange={handleChange}
                                        rows="4"
                                        placeholder={t('pages.clients.form.observationsPlaceholder')}
                                        disabled={formLoading || isFormDisabled}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === TABS.ENDERECOS && (
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiMapPin /></div>
                            <h3 className={styles.sectionTitle}>{t('pages.clients.form.addresses')}</h3>
                            <span className={styles.sectionBadge}>{addresses.length + importerAddresses.length}</span>
                        </div>
                        <div style={{ padding: '20px' }}>
                            {/* Regular Addresses Section */}
                            <div className={styles.addressSubSection}>
                                <h4 style={{ marginBottom: '10px', color: '#2d3748' }}>
                                    {t('pages.clients.form.regularAddresses')}
                                    <span style={{ fontSize: '12px', marginLeft: '10px', color: '#718096' }}>
                                        ({addresses.length})
                                    </span>
                                </h4>

                                {!isAddingAddress && !isEditingAddress && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setNewAddress(NEW_ADDRESS_MODEL);
                                            setNewAddrContact(NEW_ADDR_CONTACT_MODEL);
                                            setEditingAddressIndex(null);
                                            setEditingAddressType('regular');
                                            setIsEditingAddress(false);
                                            setIsAddingAddress(true);
                                        }}
                                        className={styles.addButton}
                                        disabled={formLoading}
                                        style={{ marginBottom: '15px' }}
                                    >
                                        + {t('pages.clients.form.newAddress')}
                                    </button>
                                )}

                                {isAddingAddress && renderAddressForm(false)}

                                {addresses.length > 0 && (
                                    <div className={styles.addressGrid} style={{ marginTop: '10px' }}>
                                        {addresses.map((addr, i) => {
                                            // Skip if this address is being edited
                                            if (isEditingAddress && editingAddressIndex === i && editingAddressType === 'regular') return null;

                                            return (
                                                <div key={i} className={styles.addressCard}>
                                                    <div className={styles.addressCardHeader}>
                                                        <span className={styles.addressType}>
                                                            {addr.tipo || t('pages.clients.form.address')}
                                                        </span>
                                                        <div className={styles.addressActions}>
                                                            {can.edit && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleEditAddress(i, false)}
                                                                    className={styles.editAddressButton}
                                                                    disabled={formLoading}
                                                                    title={t('common.edit')}
                                                                >
                                                                    <FiEdit size={14} />
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveAddress(i)}
                                                                className={styles.removeAddressButton}
                                                                disabled={formLoading}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {renderAddressCardContent(addr)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <hr style={{ margin: '30px 0', border: '1px dashed #e2e8f0' }} />

                            {/* Importer Addresses Section */}
                            <div className={styles.addressSubSection}>
                                <h4 style={{ marginBottom: '10px', color: '#2d3748' }}>
                                    {t('pages.clients.form.importerAddresses')}
                                    <span style={{ fontSize: '12px', marginLeft: '10px', color: '#718096' }}>
                                        ({importerAddresses.length})
                                    </span>
                                </h4>

                                {!isAddingImporterAddress && !isEditingAddress && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setNewImporterAddress(NEW_IMPORTER_ADDRESS_MODEL);
                                            setNewAddrContact(NEW_ADDR_CONTACT_MODEL);
                                            setEditingAddressIndex(null);
                                            setEditingAddressType('importer');
                                            setIsEditingAddress(false);
                                            setIsAddingImporterAddress(true);
                                        }}
                                        className={styles.addButton}
                                        disabled={formLoading}
                                        style={{ marginBottom: '15px' }}
                                    >
                                        + {t('pages.clients.form.newImporterAddress')}
                                    </button>
                                )}

                                {isAddingImporterAddress && renderAddressForm(true)}

                                {importerAddresses.length > 0 && (
                                    <div className={styles.addressGrid} style={{ marginTop: '10px' }}>
                                        {importerAddresses.map((addr, i) => {
                                            // Skip if this address is being edited
                                            if (isEditingAddress && editingAddressIndex === i && editingAddressType === 'importer') return null;

                                            return (
                                                <div key={i} className={styles.addressCard} style={{ borderColor: '#48bb78' }}>
                                                    <div className={styles.addressCardHeader}>
                                                        <span className={styles.addressType} style={{ backgroundColor: '#48bb78', color: 'white' }}>
                                                            🏭 {addr.tipo || t('pages.clients.form.importerAddress')}
                                                        </span>
                                                        <div className={styles.addressActions}>
                                                            {can.edit && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleEditAddress(i, true)}
                                                                    className={styles.editAddressButton}
                                                                    disabled={formLoading}
                                                                    title={t('common.edit')}
                                                                >
                                                                    <FiEdit size={14} />
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveImporterAddress(i)}
                                                                className={styles.removeAddressButton}
                                                                disabled={formLoading}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {renderAddressCardContent(addr)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {addresses.length === 0 && importerAddresses.length === 0 && !isAddingAddress && !isAddingImporterAddress && (
                                <div className={styles.emptyState} style={{ marginTop: '20px' }}>
                                    <p className={styles.emptyMessage}>{t('pages.clients.form.noAddresses')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.pageContainer}>
            {/* Toast Notifications */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => removeToast(toast.id)}
                        />
                    ))}
                </div>
            )}

            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={resetForm}
                onSubmit={handleSave}
                title={editingItem ? t('pages.clients.editClient') : t('pages.clients.newClient')}
                subtitle={editingItem ? `${t('pages.clients.editingLabel')}: ${formData.razaoSocial || editingItem.razaoSocial} (${formData.codigo || editingItem.codigo})` : undefined}
                isLoading={formLoading}
                error={saveError}
                submitText={editingItem ? t('common.saveChanges') : t('common.create')}
                maxWidth="900px"
            >
                {renderModalContent()}
            </FormModal>

            {/* Page Header */}
            <PageHeader
                title={t('pages.clients.title')}
                actionText={can.create ? t('pages.clients.newClient') : undefined}
                onAction={handleNewClick}
                disabled={listLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <div className={styles.inputGroup} style={{ flex: 1 }}>
                    <label htmlFor="clientSearch">{t('pages.clients.search.placeholder')}</label>
                    <SearchBar
                        id="clientSearch"
                        placeholder={t('pages.clients.search.placeholder')}
                        onSearch={handleSearch}
                        disabled={listLoading}
                        debounceDelay={300}
                        initialValue={searchTerm}
                    />
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
                {listLoading ? (
                    <div>
                        <div className={styles.emptyTable}>{t('pages.clients.table.loading')}</div>
                        <TableSkeleton columns={7} rows={ITEMS_PER_PAGE} />
                    </div>
                ) : (
                    renderTable()
                )}
            </div>

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredClients?.length || 0}
                onPageChange={goToPage}
                disabled={listLoading}
                itemsLabel={t('pages.clients.table.items')}
            />
        </div>
    );
};

export default ClientsPage;